import { ActionIcon, Badge, Button, Card, Flex, Text, Tooltip } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import type { PredefinedIcons } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.types';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import {
	PersonCardAction,
	PersonStatus,
	PersonTag,
} from '@dealstack/frontend/shared/components/ExpandablePersonCard/ExpandablePersonCard.types';
import type { GroupMenuItem } from '@dealstack/frontend/shared/components/ExpandablePersonCard/GroupHeader.types';
import { SignerStatusPill } from '@dealstack/frontend/shared/components/ExpandablePersonCard/SignerStatusPill';
import { PersonCardMenu } from '@dealstack/frontend/shared/components/ExpandablePersonCard/PersonCardMenu';

interface Props {
	name: string;
	email?: string | null;
	status?: PersonStatus;
	tags?: PersonTag[];
	subtitle?: string | null;
	subtitleColor?: string;
	action?: PersonCardAction;
	menuItems?: GroupMenuItem[];
	menuTestId?: string;
	expanded: boolean;
	onToggle: () => void;
	expandable?: boolean;
}

export const ExpandablePersonCard = ({
	name,
	email,
	status,
	tags,
	subtitle,
	subtitleColor,
	action,
	menuItems,
	menuTestId,
	expanded,
	onToggle,
	expandable = false,
}: Props) => {
	return (
		<Card
			p="0.5rem"
			style={{
				border: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
				borderRadius: '0.25rem',
			}}
		>
			<Flex direction="column" gap="0.5rem">
				<Flex justify="space-between" align="stretch">
					<Flex direction="column" gap="0.25rem" style={{ flex: 1, minWidth: 0 }}>
						<Flex gap="0.375rem" align="center" wrap="wrap">
							<Tooltip label={name} openDelay={100} withinPortal>
								<Text
									fw={600}
									size="0.875rem"
									lh="1.25rem"
									truncate="end"
									style={{ maxWidth: '100%' }}
								>
									{name}
								</Text>
							</Tooltip>
							{tags?.map((tag) => (
								<Badge
									key={tag.label}
									size="xs"
									radius="sm"
									color="gray.2"
									leftSection={
										tag.icon ? (
											<Icon
												variant={tag.icon as PredefinedIcons}
												size="md"
												color={defaultTheme.v3.colors.dark[0]}
											/>
										) : undefined
									}
								>
									<Text size="0.75rem" fw={400} lh="1rem" c="dark.0">
										{tag.label}
									</Text>
								</Badge>
							))}
						</Flex>

						<Flex gap="0.5rem" align="center">
							{email && (
								<Tooltip label={email} openDelay={100} withinPortal>
									<Text
										size="0.875rem"
										c="dark.0"
										lh="1.25rem"
										truncate="end"
										style={{ maxWidth: '100%' }}
									>
										{email}
									</Text>
								</Tooltip>
							)}
							{status && <SignerStatusPill status={status} />}
						</Flex>

						{subtitle && (
							<Text size="0.75rem" c={subtitleColor ?? 'blue.6'} fw={500} lh="1rem">
								{subtitle}
							</Text>
						)}
					</Flex>

					{menuItems && menuItems.length > 0 && (
						<PersonCardMenu menuItems={menuItems} menuTestId={menuTestId} />
					)}
					{expandable && (
						<ActionIcon
							variant="subtle"
							size="sm"
							onClick={onToggle}
							style={{ alignSelf: 'center', marginLeft: '0.5rem' }}
						>
							<Icon
								variant={expanded ? 'caret-down' : 'caret-left'}
								size="lg"
								color={defaultTheme.v3.colors.black[0]}
							/>
						</ActionIcon>
					)}
				</Flex>

				{action && (
					<Flex>
						<Tooltip
							label={action.disabledReason}
							disabled={!action.disabledReason || !action.disabled}
							withinPortal
						>
							<span>
								<Button
									size="xs"
									disabled={action.disabled}
									onClick={action.onClick}
									data-testid={action.testId}
								>
									{action.label}
								</Button>
							</span>
						</Tooltip>
					</Flex>
				)}
			</Flex>
		</Card>
	);
};
